import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Star, Film, Tv, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StreamingPlatforms } from "@/components/StreamingPlatforms";

// Helper to extract YouTube video ID
function getYouTubeId(url) {
  if (!url) return null;
  const m1 = url.match(/[?&]v=([^&]+)/);
  const m2 = url.match(/youtu\.be\/([^?&]+)/);
  const m3 = url.match(/embed\/([^?&]+)/);
  if (m1) return m1[1];
  if (m2) return m2[1];
  if (m3) return m3[1];
  return null;
}

// Helper to extract Vimeo video ID
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export const Hero = ({ movie }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trailerUrl, setTrailerUrl] = useState(
    movie?.trailer_url || movie?.video_url || movie?.trailer || ""
  );
  const [isMuted, setIsMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const handleWatch = (e) => {
    if (!user) {
      e.preventDefault();
      toast.info("Regístrate o inicia sesión para ver cualquier película o serie");
      navigate("/auth", { state: { from: to } });
    }
  };

  // Fetch trailer URL from media endpoint if not directly provided in movie object
  useEffect(() => {
    if (!movie) return;
    const initialUrl = movie.trailer_url || movie.video_url || movie.trailer || "";
    setTrailerUrl(initialUrl);
    setVideoLoaded(false);

    if (!initialUrl && movie.id) {
      api
        .get(`/movies/${movie.id}/media`)
        .then(({ data }) => {
          if (data?.trailer_url) {
            setTrailerUrl(data.trailer_url);
          }
        })
        .catch(() => {});
    }
  }, [movie]);

  if (!movie) return null;

  const to = `/titulo/${movie.slug}`;
  const ytId = getYouTubeId(trailerUrl);
  const vmId = getVimeoId(trailerUrl);
  const isDirectVideo = trailerUrl && !ytId && !vmId && /\.(mp4|webm|m3u8)/.test(trailerUrl);
  const hasBackgroundVideo = Boolean(ytId || vmId || isDirectVideo);

  return (
    <div
      className="relative h-[78vh] sm:h-[85vh] md:h-[90vh] w-full overflow-hidden bg-black select-none preserve-dark"
      data-testid="hero-billboard"
    >
      {/* 1. Backdrop Fallback Image */}
      <div className="absolute inset-0 z-0">
        {movie.backdrop_url || movie.poster_url ? (
          <img
            src={movie.backdrop_url || movie.poster_url}
            alt={movie.title}
            className={`w-full h-full object-cover object-center transition-opacity duration-1000 ${
              videoLoaded ? "opacity-10" : "opacity-100 scale-105"
            }`}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1280&auto=format&fit=crop&q=80";
            }}
          />
        ) : (
          <div className="w-full h-full bg-zinc-950" />
        )}
      </div>

      {/* 2. Seamless Full-Coverage Video Trailer Layer */}
      {hasBackgroundVideo && (
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none flex items-center justify-center">
          {ytId && (
            <iframe
              key={`${ytId}-${isMuted}`}
              src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=${
                isMuted ? 1 : 0
              }&controls=0&loop=1&playlist=${ytId}&playsinline=1&enablejsapi=1&disablekb=1&fs=0&modestbranding=1&iv_load_policy=3&autohide=1&showsearch=0&rel=0`}
              title={movie.title}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350vw] h-[196.875vw] min-h-[140dvh] min-w-[248.88dvh] pointer-events-none border-0 opacity-90 transition-opacity duration-1000 scale-105"
              allow="autoplay; encrypted-media; picture-in-picture"
              onLoad={() => setVideoLoaded(true)}
            />
          )}

          {vmId && (
            <iframe
              key={`${vmId}-${isMuted}`}
              src={`https://player.vimeo.com/video/${vmId}?background=1&autoplay=1&loop=1&byline=0&title=0&muted=${
                isMuted ? 1 : 0
              }`}
              title={movie.title}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350vw] h-[196.875vw] min-h-[140dvh] min-w-[248.88dvh] pointer-events-none border-0 opacity-90 transition-opacity duration-1000 scale-105"
              allow="autoplay; encrypted-media"
              onLoad={() => setVideoLoaded(true)}
            />
          )}

          {isDirectVideo && (
            <video
              src={trailerUrl}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover opacity-90"
              onLoadedData={() => setVideoLoaded(true)}
            />
          )}
        </div>
      )}

      {/* 3. Ultra-Clean Cinematic Overlay Gradients */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/50 to-transparent max-w-3xl" />
      </div>

      {/* 4. Ultra-Clean Minimal Content Layout */}
      <div className="relative z-20 h-full flex flex-col justify-end pb-12 sm:pb-16 md:pb-24 px-6 md:px-16 max-w-3xl pt-24">
        {/* Category Pill (Desktop only) */}
        <div className="hidden sm:flex items-center gap-2 mb-3">
          <span className="bg-black/60 backdrop-blur-md text-zinc-300 border border-white/15 text-[10px] sm:text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5">
            {movie.media_type === "tv" ? (
              <Tv size={12} className="text-blue-400" />
            ) : (
              <Film size={12} className="text-[#E11D48]" />
            )}
            {movie.media_type === "tv" ? "Serie LGBT+" : "Película LGBT+"}
          </span>
          {(movie.custom_badge || movie.upcoming) && (
            <span className="bg-gradient-to-r from-amber-500 to-rose-500 text-white text-[10px] sm:text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5 shadow-lg border border-amber-300/30 animate-pulse">
              <Star size={11} className="fill-amber-200 text-amber-200" />
              {movie.custom_badge || t("upcoming")}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl sm:text-6xl md:text-7xl font-black tracking-tight text-white mb-2 sm:mb-3 leading-tight uppercase drop-shadow-md">
          {movie.title}
        </h1>

        {/* Metadata Row (Desktop only) */}
        <div className="hidden sm:flex flex-wrap items-center gap-2.5 text-xs sm:text-sm text-zinc-300 mb-4 font-medium">
          {movie.year && (
            <span className="bg-white/10 px-2.5 py-0.5 rounded text-white font-semibold">
              {movie.year}
            </span>
          )}
          {movie.duration && <span>· {movie.duration}</span>}
          {movie.avg_rating > 0 && (
            <span className="flex items-center gap-1 text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20 font-semibold">
              <Star size={13} className="fill-amber-400 text-amber-400" /> {movie.avg_rating}
            </span>
          )}
          {movie.genres?.slice(0, 3).map((g) => (
            <span
              key={g}
              className="text-zinc-400 bg-zinc-900/80 px-2.5 py-0.5 rounded border border-white/10"
            >
              {g}
            </span>
          ))}
        </div>

        {/* Minimal Description */}
        <p className="text-zinc-300 text-xs sm:text-base leading-relaxed line-clamp-2 md:line-clamp-3 mb-5 max-w-2xl font-normal drop-shadow-sm">
          {movie.description}
        </p>

        {/* Action Button & Streaming Platforms */}
        <div className="flex flex-wrap items-center gap-3">
          <Link
            data-testid="hero-play-button"
            to={to}
            onClick={handleWatch}
            className="bg-[#E11D48] text-white px-7 py-3 sm:px-8 sm:py-3.5 rounded-full font-bold hover:bg-[#BE123C] active:scale-95 transition-all flex items-center gap-2.5 min-h-[44px] sm:min-h-[48px] shadow-lg shadow-[#E11D48]/30 hover:shadow-[#E11D48]/50 text-sm sm:text-base shrink-0"
          >
            <Play size={18} className="fill-white ml-0.5" />
            <span>{t("watchMovie")}</span>
          </Link>

          {/* Botones de plataformas donde ver la película en el Hero */}
          <StreamingPlatforms movie={movie} variant="inline" />
        </div>
      </div>

      {/* 5. Floating Audio Control Toggle for Background Video */}
      {hasBackgroundVideo && (
        <button
          onClick={() => setIsMuted(!isMuted)}
          data-testid="hero-audio-toggle"
          className="absolute bottom-12 sm:bottom-16 md:bottom-24 right-6 md:right-16 z-30 p-2.5 sm:p-3 bg-black/60 hover:bg-black/90 text-white rounded-full border border-white/20 backdrop-blur-md transition-all shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center cursor-pointer"
          title={isMuted ? "Activar sonido" : "Silenciar"}
        >
          {isMuted ? (
            <VolumeX size={16} className="text-zinc-300" />
          ) : (
            <Volume2 size={16} className="text-[#E11D48]" />
          )}
        </button>
      )}
    </div>
  );
};
